import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Palette, Type, Navigation as NavIcon, LayoutGrid, Loader2, AlertCircle, CheckCircle, PanelBottom, FileUp, Check, MousePointerClick, Package } from 'lucide-react';
import { superAdminApi } from '../lib/api';
import { createSuperAdminKreatorAdapter, type KreatorApiAdapter } from '../lib/kreator-api-adapter';
import type { KreatorSettingsData } from './KreatorPage';
import { SettingsSaveBar, type SaveStatus } from '../components/SettingsSaveBar';
import { SectionHeader } from '../components/SectionHeader';
import { KreatorColorsSection } from './kreator/KreatorColorsSection';
import { KreatorTypographySection } from './kreator/KreatorTypographySection';
import { KreatorNavigationSection } from './kreator/KreatorNavigationSection';
import { KreatorPageAppearanceSection } from './kreator/KreatorPageAppearanceSection';
import { KreatorFooterSection } from './kreator/KreatorFooterSection';
import { lazyRetry } from '../lib/lazy-retry';

type TabKey = 'pageAppearance' | 'colors' | 'typography' | 'navigation' | 'footer' | 'bundles';

const TABS: { key: TabKey; label: string; icon: typeof LayoutGrid }[] = [
  { key: 'pageAppearance', label: 'Wygląd stron', icon: LayoutGrid },
  { key: 'colors', label: 'Kolory', icon: Palette },
  { key: 'typography', label: 'Typografia', icon: Type },
  { key: 'navigation', label: 'Nawigacja', icon: NavIcon },
  { key: 'footer', label: 'Stopka', icon: PanelBottom },
  { key: 'bundles', label: 'Zestawy', icon: Package },
];

const BundleSettingsEditor = lazy(() =>
  lazyRetry(() => import('./kreator/bundle/BundleSettingsPage').then(module => ({ default: module.BundleSettingsEditor }))),
);

/**
 * Edycja globalnych domyślnych ustawień Kreatora dla wszystkich sklepów.
 * System-scoped DB config - każdy nowy superadmin i reset tenanta widzi te
 * same wartości. Sklepy używają "Przywróć domyślne" w panelu Kreator →
 * Import / Eksport żeby skopiować te wartości do swoich settings.
 *
 * Re-używa istniejące Kreator*Section.tsx przez `apiAdapter` prop -
 * bez duplikowania kodu UI.
 */
export function AdminKreatorDefaultsPage() {
  const [settings, setSettings] = useState<KreatorSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('pageAppearance');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const adapter = useMemo(() => createSuperAdminKreatorAdapter(), []);

  const reload = useCallback(() => {
    setLoading(true);
    superAdminApi.getKreatorDefaults()
      .then(res => {
        setSettings(res.data as KreatorSettingsData);
        setError(null);
      })
      .catch(() => setError('Nie udało się pobrać domyślnych ustawień'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const showSuccess = useCallback((msg: string) => setToast({ type: 'success', message: msg }), []);
  const showError = useCallback((msg: string) => setToast({ type: 'error', message: msg }), []);

  if (loading && !settings) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-lg bg-danger/10 border border-danger/30 text-sm text-danger">
        <AlertCircle className="w-4 h-4" /> {error}
      </div>
    );
  }

  // sectionProps są te same dla każdej sekcji - sekcje używają `apiAdapter`
  // zamiast `kreatorApi.X(integrationId, ...)`. integrationId jest tu placeholderem
  // wymaganym przez stary type sekcji (używany tylko przy uploads, których
  // super-admin nie ma) - faktyczne wywołania zapisu idą przez adapter.
  const sectionProps = {
    integrationId: '__defaults__',
    settings,
    onSaved: reload,
    showSuccess,
    showError,
    apiAdapter: adapter,
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Domyślne ustawienia Kreatora</h2>
        <p className="text-sm text-text-secondary mt-1">
          Globalne domyślne wartości dla wszystkich sklepów. Każdy nowy sklep startuje z tymi ustawieniami.
          Sklepy mogą wrócić do nich w dowolnym momencie przez "Import / Eksport" w ustawieniach Kreatora.
        </p>
      </div>

      {/* Tab bar - poziomy */}
      <div className="flex items-center gap-1 bg-surface-raised/50 p-1 rounded-xl border border-border-subtle w-fit overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Active section */}
      <div>
        {activeTab === 'pageAppearance' && <KreatorPageAppearanceSection {...sectionProps} />}
        {activeTab === 'colors' && (
          <div className="space-y-6">
            <KreatorColorsSection {...sectionProps} showButtonColors={false} />
            <AdminButtonColorsDefaultsSection
              settings={settings}
              apiAdapter={adapter}
              onSaved={reload}
              showSuccess={showSuccess}
              showError={showError}
            />
          </div>
        )}
        {activeTab === 'typography' && <KreatorTypographySection {...sectionProps} />}
        {activeTab === 'navigation' && <KreatorNavigationSection {...sectionProps} />}
        {activeTab === 'footer' && (
          <KreatorFooterSection
            {...sectionProps}
            systemFooterUploader={<SystemFooterUploader showSuccess={showSuccess} showError={showError} />}
          />
        )}
        {activeTab === 'bundles' && (
          <AdminBundleDefaultsSection showSuccess={showSuccess} showError={showError} />
        )}
      </div>

      {/* Toast — bottom-right pop */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
          <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border backdrop-blur-sm ${
            toast.type === 'success'
              ? 'bg-success/15 border-success/30 text-success'
              : 'bg-danger/15 border-danger/30 text-danger'
          }`}>
            {toast.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="text-sm font-medium">{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 opacity-60 hover:opacity-100 text-lg leading-none">&times;</button>
          </div>
        </div>
      )}
    </div>
  );
}

type ButtonColorDefaultsForm = {
  buttonBackgroundColor: string;
  buttonBackgroundHoverColor: string;
  buttonTextColor: string;
  buttonTextHoverColor: string;
};

function getButtonColorDefaultsForm(settings: KreatorSettingsData | null): ButtonColorDefaultsForm {
  const settingsRecord = (settings?.settings ?? {}) as Record<string, unknown>;
  return {
    buttonBackgroundColor: (settingsRecord.buttonBackgroundColor as string) || '#3b82f6',
    buttonBackgroundHoverColor: (settingsRecord.buttonBackgroundHoverColor as string) || '#3b82f6',
    buttonTextColor: (settingsRecord.buttonTextColor as string) || '#ffffff',
    buttonTextHoverColor: (settingsRecord.buttonTextHoverColor as string) || '#ffffff',
  };
}

function AdminBundleDefaultsSection({
  showSuccess,
  showError,
}: {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}) {
  const [settings, setSettings] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    superAdminApi.getBundleDefaults()
      .then(res => {
        setSettings(res.data.settings);
        setError(null);
      })
      .catch(() => setError('Nie udało się pobrać domyślnych ustawień zestawów'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading && !settings) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-lg bg-danger/10 border border-danger/30 text-sm text-danger">
        <AlertCircle className="w-4 h-4" /> {error}
      </div>
    );
  }

  return (
    <Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-accent-primary" /></div>}>
      <BundleSettingsEditor
        initialSettings={settings ?? {}}
        title="Domyślne ustawienia zestawów"
        description="Globalne ustawienia zestawów używane jako start dla sklepów i jako baza konfiguracji"
        onSave={async (nextSettings) => {
          try {
            const res = await superAdminApi.updateBundleDefaults(nextSettings);
            setSettings(res.data.settings);
            showSuccess('Zapisano domyślne ustawienia zestawów');
          } catch {
            showError('Nie udało się zapisać domyślnych ustawień zestawów');
            throw new Error('Bundle defaults save failed');
          }
        }}
      />
    </Suspense>
  );
}

function AdminButtonColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-12 rounded-lg border border-border bg-transparent cursor-pointer"
      />
      <span className="text-xs text-text-secondary text-center leading-tight">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input w-24 text-xs font-mono text-center"
      />
    </div>
  );
}

function AdminButtonColorsDefaultsSection({
  settings,
  apiAdapter,
  onSaved,
  showSuccess,
  showError,
}: {
  settings: KreatorSettingsData | null;
  apiAdapter: KreatorApiAdapter;
  onSaved: () => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}) {
  const [form, setForm] = useState<ButtonColorDefaultsForm>(() => getButtonColorDefaultsForm(settings));
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  useEffect(() => {
    setForm(getButtonColorDefaultsForm(settings));
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiAdapter.updateSettings({
        buttonBackgroundColor: form.buttonBackgroundColor || '#3b82f6',
        buttonBackgroundHoverColor: form.buttonBackgroundHoverColor || '#3b82f6',
        buttonTextColor: form.buttonTextColor || '#ffffff',
        buttonTextHoverColor: form.buttonTextHoverColor || '#ffffff',
      });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      showSuccess('Zapisano domyślne kolory buttonów');
      onSaved();
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
      showError('Nie udało się zapisać domyślnych kolorów buttonów');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="card">
        <SectionHeader
          icon={MousePointerClick}
          title="Kolory buttonów"
          description="Globalne domyślne kolory tła i tekstu przycisków w sklepach"
          accent="secondary"
        />
        <div className="flex flex-wrap gap-6">
          <AdminButtonColorInput
            label="Tło"
            value={form.buttonBackgroundColor}
            onChange={(value) => setForm({ ...form, buttonBackgroundColor: value })}
          />
          <AdminButtonColorInput
            label="Tło hover"
            value={form.buttonBackgroundHoverColor}
            onChange={(value) => setForm({ ...form, buttonBackgroundHoverColor: value })}
          />
          <AdminButtonColorInput
            label="Tekst"
            value={form.buttonTextColor}
            onChange={(value) => setForm({ ...form, buttonTextColor: value })}
          />
          <AdminButtonColorInput
            label="Tekst hover"
            value={form.buttonTextHoverColor}
            onChange={(value) => setForm({ ...form, buttonTextHoverColor: value })}
          />
        </div>
      </div>

      <SettingsSaveBar
        saving={saving}
        status={saveStatus}
        onSave={handleSave}
        label="Zapisz kolory buttonów"
      />
    </div>
  );
}

// ─── SystemFooterUploader (custom mode w panelu super-admina) ───────────────
// Wgrywa BlockNode[] z eksportu buildera. Nadpisuje data/default-footer.json.
// Każdy reset-do-domyślnych w panelu sklepu kopiuje tę zawartość do tenant'a.

function SystemFooterUploader({ showSuccess, showError }: {
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}) {
  const [summary, setSummary] = useState<{ blocks: number } | null>(null);
  const [parsedContent, setParsedContent] = useState<unknown[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const reload = useCallback(() => {
    superAdminApi.getFooterContentSummary()
      .then(res => setSummary(res.data))
      .catch(() => setSummary(null));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(null);
    setParsedContent(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (!Array.isArray(parsed) || parsed.length === 0) {
          setParseError('Plik musi zawierać niepustą tablicę BlockNode[] (eksport z buildera).');
          return;
        }
        setParsedContent(parsed);
      } catch {
        setParseError('Nieprawidłowy format JSON.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!parsedContent) return;
    setUploading(true);
    try {
      const res = await superAdminApi.uploadFooterContent(parsedContent);
      showSuccess(`Wgrano globalną stopkę (${res.data.blocks} bloków). Każdy reset-do-domyślnych w sklepach skopiuje ją do tenant'a.`);
      setParsedContent(null);
      setFileName('');
      reload();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showError(msg ?? 'Nie udało się wgrać stopki');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Aktualny stan */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-surface-base/40 border border-border-subtle">
        <div className="p-2 rounded-lg bg-accent-info/10 shrink-0">
          <PanelBottom className="w-4 h-4 text-accent-info" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-text-primary">Aktualna globalna stopka</p>
          <p className="text-xs text-text-secondary mt-0.5">
            {summary === null
              ? 'Sprawdzanie...'
              : `Plik default-footer.json zawiera ${summary.blocks} ${summary.blocks === 1 ? 'block' : 'bloków'} treści.`}
          </p>
        </div>
      </div>

      {/* Workflow */}
      <div className="text-xs text-text-muted space-y-1 pl-2">
        <p><strong className="text-text-secondary">Workflow:</strong></p>
        <p>1. Otwórz Kreator w swoim sklepie testowym → Stopka → Spersonalizowana → Otwórz edytor</p>
        <p>2. Zaprojektuj domyślną stopkę EcomFlow</p>
        <p>3. W toolbarze: Import / Eksport → Eksport (pobierz plik JSON)</p>
        <p>4. Wgraj plik tutaj - zostanie zapisany jako globalny default dla wszystkich sklepów</p>
      </div>

      {/* Upload form */}
      <div className="p-4 rounded-lg border border-dashed border-border-subtle bg-surface-raised">
        <label className="block text-xs text-text-secondary mb-2">Plik JSON (eksport z EcomFlow Buildera)</label>
        <input
          type="file"
          accept="application/json,.json"
          onChange={handleFile}
          className="w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-accent-primary/15 file:text-accent-primary file:cursor-pointer file:text-xs"
        />
        {fileName && parsedContent && (
          <p className="text-[11px] text-success mt-2 flex items-center gap-1">
            <Check className="w-3 h-3" /> {fileName} - {parsedContent.length} block(ów) gotowe do wgrania
          </p>
        )}
        {parseError && (
          <p className="text-[11px] text-danger mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {parseError}
          </p>
        )}

        <button
          onClick={handleUpload}
          disabled={!parsedContent || uploading}
          className="btn-primary text-xs flex items-center gap-1.5 mt-3 px-4 py-2 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
          Wgraj jako globalną stopkę
        </button>
      </div>
    </div>
  );
}
